import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Award, CheckCircle } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const values = [
    {
      icon: <Target size={40} />,
      title: 'Accuracy',
      description: 'We ensure 100% accuracy in all tax filings and compliance matters.',
    },
    {
      icon: <CheckCircle size={40} />,
      title: 'Integrity',
      description: 'Transparent and honest services with complete client confidentiality.',
    },
    {
      icon: <Award size={40} />,
      title: 'Excellence',
      description: 'Committed to delivering exceptional service quality and expertise.',
    },
    {
      icon: <Eye size={40} />,
      title: 'Trust',
      description: 'Building long-term relationships based on trust and reliability.',
    },
  ];

  return (
    <div className="pt-32" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight" data-testid="about-hero-title">
              About Anku Tax Consultant
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Your trusted partner for comprehensive GST and Income Tax services in Indore. We provide professional tax consulting solutions tailored to your needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-white" data-testid="who-we-are-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1758519288905-38b7b00c1023?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHx0YXglMjBmaW5hbmNpYWwlMjBjb25zdWx0aW5nJTIwcHJvZmVzc2lvbmFsJTIwc2hha2luZyUyMGhhbmRzfGVufDB8fHx8MTc3NTU1MzQxMnww&ixlib=rb-4.1.0&q=85"
                alt="Professional Team"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" data-testid="who-we-are-title">
                Who We Are
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Anku Tax Consultant is a leading tax consulting firm based in Indore, specializing in GST and Income Tax services. With over 10 years of experience, we have successfully served more than 500 clients across various industries.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Our team of qualified professionals is dedicated to providing accurate, timely, and cost-effective tax solutions. We stay updated with the latest tax laws and regulations to ensure complete compliance for our clients.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Whether you're an individual taxpayer, a small business, or a large enterprise, we have the expertise to handle all your tax-related needs with utmost professionalism and confidentiality.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl p-10 shadow-lg border border-slate-100"
              data-testid="mission-card"
            >
              <div className="text-[#2D3C92] mb-4 floating">
                <Target size={48} />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Mission</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                To provide exceptional tax consulting services that simplify compliance, minimize tax liabilities, and maximize savings for our clients. We aim to be the most trusted tax consulting partner by delivering accurate, timely, and professional services.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl p-10 shadow-lg border border-slate-100"
              data-testid="vision-card"
            >
              <div className="text-[#10B981] mb-4 floating">
                <Eye size={48} />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Vision</h3>
              <p className="text-base text-slate-600 leading-relaxed">
                To become the leading tax consulting firm in Madhya Pradesh, recognized for our expertise, integrity, and commitment to client success. We envision a future where tax compliance is seamless and stress-free for every business and individual.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" data-testid="values-title">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide our work and relationships with clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all"
                data-testid={`value-card-${index}`}
              >
                <div className="text-[#2D3C92] mb-4 floating">{value.icon}</div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" data-testid="location-title">
              Visit Our Office
            </h2>
            <p className="text-base text-slate-600 mb-4">
              Shop no. 3&4, 3rd Floor, Plot no. 53, Scheme NO. 53,<br />
              Ratnalok Colony Vijay Nagar Indore. (Pin Code 452011)
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <a
                href="tel:6268642514"
                className="bg-[#2D3C92] text-white hover:bg-[#1e2a6b] px-6 py-3 rounded-full font-medium transition-all shadow-md hover:shadow-lg"
                data-testid="about-call-button"
              >
                Call Us: 6268642514
              </a>
              <a
                href="mailto:VERMATAXCON121@GMAIL.COM"
                className="bg-[#10B981] text-white hover:bg-[#047857] px-6 py-3 rounded-full font-medium transition-all shadow-md hover:shadow-lg"
                data-testid="about-email-button"
              >
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;